import { useEffect, useRef, useState, useCallback } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { SocialProof } from "@/components/SocialProof";
import { Packages } from "@/components/Packages";
import { AddOns } from "@/components/AddOns";
import { Events } from "@/components/Events";
import { Team } from "@/components/Team";
import { Gallery } from "@/components/Gallery";
import { Faq } from "@/components/Faq";
import { QuoteModal } from "@/components/QuoteModal";
import { Footer } from "@/components/Footer";

const NOISE_BG =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

function App() {
  const lenisRef = useRef(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [tier, setTier] = useState("unsure");

  useEffect(() => {
    const lenis = new Lenis({ duration: 0.9, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (!lenisRef.current) return;
    if (quoteOpen) lenisRef.current.stop();
    else lenisRef.current.start();
  }, [quoteOpen]);

  const scrollTo = useCallback((target) => {
    lenisRef.current?.scrollTo(target, { offset: -71, duration: 1.2 });
  }, []);

  const openQuote = useCallback((t = "unsure") => {
    setTier(t);
    setQuoteOpen(true);
  }, []);

  return (
    <div className="bg-ink text-slate-50 min-h-screen">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[100] opacity-[0.04]"
        style={{ backgroundImage: NOISE_BG }}
      />
      <Navbar onNavigate={scrollTo} onQuote={() => openQuote()} />
      <main>
        <Hero onNavigate={scrollTo} onQuote={() => openQuote()} />
        <Marquee />
        <SocialProof onNavigate={scrollTo} />
        <Packages onQuote={openQuote} />
        <AddOns />
        <Events />
        <Team />
        <Gallery />
        <Faq />
      </main>
      <Footer onQuote={() => openQuote()} />
      <QuoteModal
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        tier={tier}
        onTierChange={setTier}
      />
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
